import { memo, useEffect } from '../../lib/teact/teact';
import { getActions, getGlobal, withGlobal } from '../../global';

import type { TabState } from '../../global/types';
import { ApiMediaFormat } from '../../api/types';

import { selectTabState } from '../../global/selectors';
import { IS_TAURI } from '../../util/browser/globalEnvironment';
import {
  IS_NATIVE_SAVE_REQUIRED, IS_OPFS_SUPPORTED, IS_SERVICE_WORKER_SUPPORTED, MAX_BUFFER_SIZE,
} from '../../util/browser/windowEnvironment';
import download from '../../util/download';
import {
  claimPendingSaveDirectory, saveToDirectory, takeSaveDirectory,
} from '../../util/fileSaveHandles';
import generateUniqueId from '../../util/generateUniqueId';
import * as mediaLoader from '../../util/mediaLoader';
import saveToDownloads from '../../util/tauri/saveToDownloads';

import useLastCallback from '../../hooks/useLastCallback';
import useRunDebounced from '../../hooks/useRunDebounced';

type StateProps = {
  activeDownloads: TabState['activeDownloads'];
};

const GLOBAL_UPDATE_DEBOUNCE = 1000;

const processedHashes = new Set<string>();
const downloadedHashes = new Set<string>();

const DownloadManager = ({
  activeDownloads,
}: StateProps) => {
  const { cancelMediaHashDownloads, markMediaDownloaded, showNotification } = getActions();

  const runDebounced = useRunDebounced(GLOBAL_UPDATE_DEBOUNCE, true);

  const handleMediaDownloaded = useLastCallback((hash: string) => {
    downloadedHashes.add(hash);
    runDebounced(() => {
      if (downloadedHashes.size) {
        cancelMediaHashDownloads({ mediaHashes: Array.from(downloadedHashes) });
        downloadedHashes.clear();
      }
    });
  });

  // Writes the result to disk: into the folder picked for a batch, otherwise a normal browser download.
  // Marks the media as downloaded only when it is actually saved.
  const finishDownload = useLastCallback((
    hash: string, sourceUrl: string, filename: string, isExternalUrl: boolean,
  ) => {
    // A batch download the user pointed at a folder: write into it instead of handing each file to
    // the browser's download manager, which would prompt per file.
    const directory = takeSaveDirectory(hash);
    if (directory) {
      saveToDirectory(directory, filename, sourceUrl)
        .then((isSaved) => {
          if (isSaved) {
            markMediaDownloaded({ hash });
          } else {
            showNotification({ message: { key: 'FileSaveFailed' } });
          }
        })
        .catch(() => undefined)
        .finally(() => handleMediaDownloaded(hash));
      return;
    }

    // macOS desktop: no download manager to hand the file to, so write it and confirm it ourselves.
    // This also covers `isExternalUrl`, whose `window.open` the shell turns into a new app window.
    if (IS_NATIVE_SAVE_REQUIRED) {
      saveToDownloads(sourceUrl, filename)
        .then((savedName) => {
          if (!savedName) {
            showNotification({ message: { key: 'NativeDownloadFailed' } });
            return;
          }
          markMediaDownloaded({ hash, fileName: savedName });
          showNotification({ message: { key: 'NativeDownloadFinished' } });
        })
        .finally(() => handleMediaDownloaded(hash));
      return;
    }

    if (isExternalUrl) {
      const downloadWindow = window.open(sourceUrl);
      downloadWindow?.addEventListener('beforeunload', () => {
        showNotification({
          message: { key: 'DownloadStartedNotification' },
        });
      }, { once: true });
    } else {
      download(sourceUrl, filename);
    }

    // A browser download can't confirm the save, so mark on trigger
    if (!IS_TAURI) markMediaDownloaded({ hash });
    handleMediaDownloaded(hash);
  });

  useEffect(() => {
    if (!Object.keys(activeDownloads).length) {
      processedHashes.clear();
      return;
    }

    // A folder picked for this batch covers exactly the downloads queued alongside it — the action
    // adds them all in one `setGlobal`, so they arrive here together.
    claimPendingSaveDirectory(Object.keys(activeDownloads).filter((hash) => !processedHashes.has(hash)));

    Object.entries(activeDownloads).forEach(([mediaHash, metadata]) => {
      if (processedHashes.has(mediaHash)) {
        return;
      }
      processedHashes.add(mediaHash);

      const { size, filename, format: mediaFormat } = metadata;

      const mediaData = mediaLoader.getFromMemory(mediaHash);

      if (mediaData) {
        finishDownload(mediaHash, mediaData, filename, false);
        return;
      }

      if (size > MAX_BUFFER_SIZE && !IS_OPFS_SUPPORTED && !IS_SERVICE_WORKER_SUPPORTED) {
        showNotification({
          message: { key: 'DownloadFileTooBig' },
        });
        handleMediaDownloaded(mediaHash);
        return;
      }

      const handleProgress = () => {
        const currentDownloads = selectTabState(getGlobal()).activeDownloads;
        if (!currentDownloads[mediaHash]) {
          mediaLoader.cancelProgress(handleProgress);
        }
      };

      mediaLoader.fetch(mediaHash, mediaFormat, true, handleProgress, generateUniqueId()).then((result) => {
        if (mediaFormat === ApiMediaFormat.DownloadUrl) {
          const url = new URL(result, window.document.baseURI);
          url.searchParams.set('filename', encodeURIComponent(filename));
          finishDownload(mediaHash, url.toString(), filename, true);
        } else if (result) {
          finishDownload(mediaHash, result, filename, false);
        } else {
          // Surface the failure — a silently cleared progress spinner reads as "nothing happened".
          showNotification({ message: { key: 'ErrorUnspecified' } });
          handleMediaDownloaded(mediaHash);
        }
      });
    });
  }, [activeDownloads]);

  return undefined;
};

export default memo(withGlobal(
  (global): Complete<StateProps> => {
    const activeDownloads = selectTabState(global).activeDownloads;

    return {
      activeDownloads,
    };
  },
)(DownloadManager));
